import { Card, CardSection, ColorField, Field, Input, Select, Slider, UploadField } from "@/components/ui";
import { contrastRatio } from "@/lib/utils/color";
import { useBranding } from "../BuilderContext";
import type { WelcomeLayout } from "@/lib/types/config";

const LAYOUTS: Array<{ value: WelcomeLayout; label: string }> = [
  { value: "split-visual", label: "Split visual" },
  { value: "classic", label: "Classic" },
  { value: "minimalist", label: "Minimalist" },
  { value: "compact", label: "Compact" },
  { value: "full-bleed", label: "Full bleed" },
];

export function BrandingTab() {
  const { branding, setBranding } = useBranding();

  const textColors = branding.text_colors ?? {};
  const textSizes = branding.text_sizes ?? {};

  const setTextColor = (key: keyof typeof textColors, value: string) =>
    setBranding({ text_colors: { ...textColors, [key]: value } });
  const setTextSize = (key: keyof typeof textSizes, value: number) =>
    setBranding({ text_sizes: { ...textSizes, [key]: value } });

  // The welcome screen paints headline text over the primary colour, so a dark
  // brand colour with dark text is a legible failure the operator can't see in
  // a small preview.
  const headlineContrast =
    branding.primary_color && textColors.headline
      ? contrastRatio(branding.primary_color, textColors.headline)
      : null;

  return (
    <div className="flex flex-col gap-8">
      <CardSection
        eyebrow="Site"
        title="Branding and welcome screen"
        description="These fields control the first impression and the public booth theme."
      >
        <Card className="grid gap-5 p-5 sm:grid-cols-2">
          <ColorField label="Primary color" value={branding.primary_color} onChange={(v) => setBranding({ primary_color: v })} />
          <ColorField label="Secondary color" value={branding.secondary_color} onChange={(v) => setBranding({ secondary_color: v })} />

          <Field label="Welcome headline">
            <Input
              placeholder="Negus Malt"
              value={branding.welcome_title ?? ""}
              onChange={(e) => setBranding({ welcome_title: e.target.value })}
            />
          </Field>

          <Field label="Welcome subtitle">
            <Input
              placeholder="des des"
              value={branding.welcome_subtitle ?? ""}
              onChange={(e) => setBranding({ welcome_subtitle: e.target.value })}
            />
          </Field>

          <Field label="Welcome tagline">
            <Input
              placeholder="Welcome"
              value={branding.welcome_tagline ?? ""}
              onChange={(e) => setBranding({ welcome_tagline: e.target.value })}
            />
          </Field>

          <Field label="CTA text" hint="Defaults to “Tap to start”.">
            <Input
              placeholder="Tap to start"
              value={branding.welcome_cta ?? ""}
              onChange={(e) => setBranding({ welcome_cta: e.target.value })}
            />
          </Field>

          <Field label="Welcome layout">
            <Select
              value={branding.welcome_layout ?? "split-visual"}
              onChange={(e) => setBranding({ welcome_layout: e.target.value as WelcomeLayout })}
            >
              {LAYOUTS.map((layout) => (
                <option key={layout.value} value={layout.value}>{layout.label}</option>
              ))}
            </Select>
          </Field>

          <Field label="App name" hint="Shown in the booth header and browser tab.">
            <Input
              placeholder="Jeba"
              value={branding.app_name ?? ""}
              onChange={(e) => setBranding({ app_name: e.target.value })}
            />
          </Field>
        </Card>
      </CardSection>

      <CardSection eyebrow="Text colors" title="Typography colours">
        <Card className="grid gap-5 p-5 sm:grid-cols-2">
          <ColorField label="Headline color" value={textColors.headline} onChange={(v) => setTextColor("headline", v)} />
          <ColorField label="Subtitle color" value={textColors.subtitle} onChange={(v) => setTextColor("subtitle", v)} />
          <ColorField label="Tagline color" value={textColors.tagline} onChange={(v) => setTextColor("tagline", v)} />
          <ColorField label="CTA text color" value={textColors.cta} onChange={(v) => setTextColor("cta", v)} />

          {headlineContrast !== null && headlineContrast < 4.5 && (
            <p className="sm:col-span-2 rounded-lg border border-warn/40 bg-warn/10 px-3 py-2 text-xs leading-relaxed text-warn">
              Headline text has a contrast ratio of {headlineContrast.toFixed(1)}:1 against the primary colour.
              Below 4.5:1 it gets hard to read on a bright kiosk screen.
            </p>
          )}
        </Card>
      </CardSection>

      <CardSection eyebrow="Text sizes" title="Scale" description="Sizes are in rem, relative to the booth's base font size.">
        <Card className="grid gap-6 p-5 sm:grid-cols-2">
          <Slider label="Headline" min={1} max={6} step={0.25} unit="rem"
            value={textSizes.headline ?? 3} onChange={(v) => setTextSize("headline", v)} />
          <Slider label="Subtitle" min={0.5} max={3} step={0.05} unit="rem"
            value={textSizes.subtitle ?? 1} onChange={(v) => setTextSize("subtitle", v)} />
          <Slider label="Tagline" min={0.5} max={3} step={0.05} unit="rem"
            value={textSizes.tagline ?? 1.25} onChange={(v) => setTextSize("tagline", v)} />
          <Slider label="CTA" min={0.75} max={3} step={0.05} unit="rem"
            value={textSizes.cta ?? 1.25} onChange={(v) => setTextSize("cta", v)} />
          <div className="sm:col-span-2">
            <Slider label="Text gap" min={0} max={4} step={0.25} unit="rem"
              value={branding.text_gap ?? 0.5} onChange={(v) => setBranding({ text_gap: v })}
              hint="Vertical spacing between the headline, subtitle and tagline." />
          </div>
        </Card>
      </CardSection>

      <CardSection eyebrow="Imagery" title="Logo and background">
        <Card className="flex flex-col gap-5 p-5">
          <UploadField
            label="Logo image"
            value={branding.logo_url}
            onChange={(value) => setBranding({ logo_url: value })}
            hint="Shown above the welcome headline. PNG with transparency works best."
          />
          <UploadField
            label="Welcome background"
            value={branding.background_url}
            accept="image/*,video/mp4,video/webm,video/ogg"
            preview={false}
            onChange={(value) => setBranding({ background_url: value, background_media_type: undefined })}
            onUpload={(asset) => setBranding({
              background_url: asset.signed_id,
              background_media_type: asset.content_type.startsWith("video/") ? "video" : "image",
            })}
            hint="Upload an image or looping video for this event's welcome background."
          />
          <UploadField
            label="Participant selection background"
            value={branding.participant_background_url}
            accept="image/*,video/mp4,video/webm,video/ogg"
            preview={false}
            onChange={(value) => setBranding({ participant_background_url: value, participant_background_media_type: undefined })}
            onUpload={(asset) => setBranding({
              participant_background_url: asset.signed_id,
              participant_background_media_type: asset.content_type.startsWith("video/") ? "video" : "image",
            })}
            hint="Optional image or looping video used only behind the participant / dream-job selection screen."
          />
          <Slider
            label="Background image opacity"
            min={0} max={1} step={0.01}
            value={branding.background_opacity ?? 0.12}
            onChange={(value) => setBranding({ background_opacity: value })}
            hint="Low values keep the background as texture behind the text; 1 shows it at full strength."
          />
        </Card>
      </CardSection>
    </div>
  );
}
